import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const MapFilters = ({ onFiltersChange, isMobile = false }) => {
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [filters, setFilters] = useState({
    serviceTypes: [],
    timeRange: '24h',
    severity: [],
    status: 'all',
    location: 'all'
  });

  const serviceTypeOptions = [
    { value: 'internet', label: 'Internet Providers', count: 89 },
    { value: 'mobile', label: 'Mobile Carriers', count: 67 },
    { value: 'streaming', label: 'Streaming Services', count: 45 },
    { value: 'cloud', label: 'Cloud Services', count: 23 },
    { value: 'financial', label: 'Financial Services', count: 12 },
    { value: 'gaming', label: 'Gaming Platforms', count: 5 }
  ];

  const timeRangeOptions = [
    { value: '1h', label: 'Last Hour' },
    { value: '6h', label: 'Last 6 Hours' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' }
  ];

  const severityOptions = [
    { value: 'critical', label: 'Critical', color: 'text-error' },
    { value: 'major', label: 'Major', color: 'text-warning' },
    { value: 'minor', label: 'Minor', color: 'text-accent' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Incidents' },
    { value: 'active', label: 'Active Only' },
    { value: 'resolved', label: 'Resolved Only' }
  ];

  const locationOptions = [
    { value: 'all', label: 'All Locations' },
    { value: 'current', label: 'Near Me (10 miles)' },
    { value: 'us-east', label: 'US East Coast' },
    { value: 'us-west', label: 'US West Coast' },
    { value: 'us-central', label: 'US Central' },
    { value: 'international', label: 'International' }
  ];

  const handleServiceTypeChange = (serviceType, checked) => {
    const newServiceTypes = checked
      ? [...filters?.serviceTypes, serviceType]
      : filters?.serviceTypes?.filter(type => type !== serviceType);
    
    const newFilters = { ...filters, serviceTypes: newServiceTypes };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSeverityChange = (severity, checked) => {
    const newSeverity = checked
      ? [...filters?.severity, severity]
      : filters?.severity?.filter(s => s !== severity);
    
    const newFilters = { ...filters, severity: newSeverity };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSelectChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    const resetFilters = {
      serviceTypes: [],
      timeRange: '24h',
      severity: [],
      status: 'all',
      location: 'all'
    };
    setFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.serviceTypes?.length > 0) count++;
    if (filters?.timeRange !== '24h') count++;
    if (filters?.severity?.length > 0) count++;
    if (filters?.status !== 'all') count++;
    if (filters?.location !== 'all') count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  if (isMobile) {
    return (
      <div className="bg-card border-b border-border">
        {/* Mobile Filter Toggle */}
        <div className="flex items-center justify-between p-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                {activeFilterCount}
              </span>
            )}
          </Button>
          
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearAllFilters}>
              Clear All
            </Button>
          )}
        </div>
        {/* Mobile Filter Panel */}
        {isExpanded && (
          <div className="px-4 pb-4 space-y-4 border-t border-border">
            <div className="grid grid-cols-1 gap-4">
              <Select
                label="Time Range"
                options={timeRangeOptions}
                value={filters?.timeRange}
                onChange={(value) => handleSelectChange('timeRange', value)}
              />
              
              <Select
                label="Status"
                options={statusOptions}
                value={filters?.status}
                onChange={(value) => handleSelectChange('status', value)}
              />
              
              <Select
                label="Location"
                options={locationOptions}
                value={filters?.location}
                onChange={(value) => handleSelectChange('location', value)}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-card border-b border-border">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Map Filters</h2>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearAllFilters}>
              <Icon name="X" size={16} className="mr-2" />
              Clear All ({activeFilterCount})
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Service Types */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-foreground">Service Types</h3>
            <div className="space-y-2">
              {serviceTypeOptions?.map((service) => (
                <Checkbox
                  key={service?.value}
                  label={
                    <div className="flex items-center justify-between w-full">
                      <span className="text-sm">{service?.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {service?.count}
                      </span>
                    </div>
                  }
                  checked={filters?.serviceTypes?.includes(service?.value)}
                  onChange={(e) => handleServiceTypeChange(service?.value, e?.target?.checked)}
                />
              ))}
            </div>
          </div>

          {/* Time Range */}
          <div className="space-y-3">
            <Select
              label="Time Range"
              options={timeRangeOptions}
              value={filters?.timeRange}
              onChange={(value) => handleSelectChange('timeRange', value)}
            />
          </div>

          {/* Severity */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-foreground">Severity</h3>
            <div className="space-y-2">
              {severityOptions?.map((severity) => (
                <Checkbox
                  key={severity?.value}
                  label={
                    <span className={`text-sm ${severity?.color}`}>
                      {severity?.label}
                    </span>
                  }
                  checked={filters?.severity?.includes(severity?.value)}
                  onChange={(e) => handleSeverityChange(severity?.value, e?.target?.checked)}
                />
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="space-y-3">
            <Select
              label="Status"
              options={statusOptions}
              value={filters?.status}
              onChange={(value) => handleSelectChange('status', value)}
            />
          </div>

          {/* Location */}
          <div className="space-y-3">
            <Select
              label="Location"
              options={locationOptions}
              value={filters?.location}
              onChange={(value) => handleSelectChange('location', value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapFilters;